<template>
    <section class="px-4 mx-auto max-w-screen-2xl md:py-28 py-14 lg:px-6 sm:px-7 md-max:pb-24">
        <div class="mx-auto md:pb-6">
            <h2
                class="mx-auto font-bold text-center title-mushroom md:px-2 font-ww-medium text-lifeboost-gray-lighter xl:px-20 xl:text-53"
                v-html="headingText">
            </h2>

            <div class="flex flex-col items-center justify-center py-8 mt-8 md:mt-12 gap-x-8 md:flex-row gap-y-2 sm:gap-y-8">
                <div class="flex items-center justify-center gap-x-3 gap-y-4 md:mr-8">
                    <div class="text-[6.563rem] font-ww-regular text-lifeboost-orange-dark">
                        {{ ratings?.avg_rating }}
                    </div>
                    <div class="flex flex-col items-start justify-between h-full">
                        <div class="mb-2 sm:mb-6">
                            <SectionMushroomNewBuilderRaitingStars :rating="5" />
                        </div>
                        <div class="leading-7 text-25 text-lifeboost-gray-lighter font-ww-regular">
                            Based on {{ ratings?.reviews_count }} reviews
                        </div>
                    </div>
                </div>
                <div class="relative flex h-full py-2.5">
                    <div class="absolute w-0.5 md:-left-8 md:block hidden h-full bottom-0 bg-[#cdcdcd]"></div>
                    <a href="https://lifeboostcoffee.com/products/cognition-mushroom-coffee#judgeme_product_reviews" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        class="px-10 py-6 text-white rounded-full bg-lifeboost-orange-dark text-25 font-ww-regular"
                        aria-label="Write a review for Lifeboost Cognition Coffee"
                        title="Write a review for Lifeboost Cognition Coffee">
                        Write a review
                    </a>
                </div>
            </div>
        </div>

        <!-- Reviews List -->
        <div class="mx-auto" ref="reviewsSection">
            <div v-for="(review, index) in reviews" :key="review.id" role="article" tabindex="0"
                :aria-labelledby="'review-' + review.id"
                class="w-full overflow-hidden border-b-2 py-14 md:py-14 bg-lifeboost-blue-lighter border-gray"
                :class="{ 'border-none': index === reviews.length - 1 }">
                <!-- Review Content -->
                <div class="grid grid-cols-12 gap-x-6 md:gap-y-0 gap-y-4">
                    <!-- Reviewer Info -->
                    <div class="flex items-center col-span-12 lg:col-span-4 xl:col-span-3 gap-x-4">
                        <div>
                            <div
                                class="flex items-center justify-center text-white rounded-full w-28 h-28 bg-lifeboost-gray">
                                <div class="flex items-center justify-center leading-none text-53">
                                    {{ review.reviewer.name[0] }}
                                </div>
                            </div>
                        </div>
                        <div class="flex flex-col w-full gap-y-3 md:w-auto">
                            <div role="heading" aria-level="2" :id="'review-' + review.id"
                                class="flex flex-wrap items-center justify-between font-heading gap-x-4 sm:flex-row">
                                <div
                                    class="font-bold text-30 text-lifeboost-gray-lighter font-ww-medium leading-38">
                                    {{ review.reviewer.name }}
                                </div>
                                <div class="text-lifeboost-gray-lighter md:hidden text-27">
                                    {{ formatDate(review.created_at) }}
                                </div>
                            </div>
                            <div>
                                <SectionMushroomNewBuilderVerified />
                            </div>
                        </div>
                    </div>

                    <!-- Review Details -->
                    <div class="grid grid-cols-12 col-span-12 mt-6 lg:col-span-8 xl:col-span-9 md:gap-x-8 md:mt-0">
                        <div class="flex flex-col justify-center col-span-12 lg:pr-10">
                            <div class="flex items-center justify-between md-max:w-full">
                                <div class="flex items-center gap-x-6 md-max:w-full md-max:inline-block">
                                    <div class="float-left md-max:pr-2">
                                        <SectionMushroomNewBuilderRaitingStars :rating="review.rating" />
                                    </div>
                                    <div class="font-bold text-lifeboost-gray-lighter font-ww-medium text-30 md-max:-mt-0.5 leading-10"
                                        v-if="review.title">
                                        {{ review.title }}
                                    </div>
                                </div>
                                <div class="hidden text-lifeboost-gray-lighter md:block font-ww-regular text-27">
                                    {{ formatDate(review.created_at) }}
                                </div>
                            </div>
                            <div class="pt-4">
                                <div class="flex flex-wrap lg:w-10/12">
                                    <div class="w-full">
                                        <div
                                            class="max-w-2xl leading-10 text-27 text-lifeboost-gray-lighter font-ww-regular">
                                            {{ review.body }}
                                        </div>
                                        <!-- <div v-if="review.pictures && review.pictures.some((el) => !el.hidden)"
                                            class="flex flex-row max-w-2xl mb-8 leading-loose">
                                            <ElementImageReviews :center="false" :pictures="review.pictures"
                                                @on-show="(index, array) => $emit('onShow', index, array)" />
                                        </div> -->
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- End of Review Content -->
            </div>

            <div class="flex justify-center mt-6 space-x-4 md:mb-10">
                <!-- Previous Button -->
                <button aria-label="Previous review" @click="changePage(currentPage - 1)" v-if="currentPage > 1 && !loading" class="pr-2">
                    <svg class="w-4 rotate-180 sm:w-auto" xmlns="http://www.w3.org/2000/svg" width="14px" height="24px" viewBox="0 0 14 24">
                        <path fill-rule="evenodd" fill="rgb(0, 0, 0)"
                            d="M13.686,11.511 C13.779,11.975 13.647,12.476 13.287,12.836 L3.457,22.665 C2.886,23.237 1.960,23.237 1.389,22.666 C0.819,22.096 0.819,21.170 1.391,20.598 L10.260,11.730 L1.384,2.854 C0.814,2.283 0.814,1.358 1.386,0.786 C1.957,0.215 2.883,0.214 3.454,0.785 L13.270,10.601 C13.524,10.855 13.658,11.179 13.686,11.511 Z" />
                    </svg>
                </button>

                <!-- Page Numbers -->
                <button
                    v-for="page in visiblePages"
                    :key="page"
                    @click="changePage(page)"
                    class="text-27"
                    :class="['cursor-pointer text-lifeboost-gray-lighter', { 'font-bold text-34 mb-1 md:pt-0': currentPage === page, 'text-gray-400 !font-medium': currentPage !== page }]"
                >
                    {{ page }}
                </button>

                <!-- Next Button -->
                <button aria-label="Next review" @click="changePage(currentPage + 1)" v-if="currentPage < totalPages && !loading" class="pl-2 text-27">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-4 sm:w-auto" width="14px" height="24px" viewBox="0 0 14 24">
                        <path fill-rule="evenodd" fill="rgb(0, 0, 0)"
                            d="M13.686,11.511 C13.779,11.975 13.647,12.476 13.287,12.836 L3.457,22.665 C2.886,23.237 1.960,23.237 1.389,22.666 C0.819,22.096 0.819,21.170 1.391,20.598 L10.260,11.730 L1.384,2.854 C0.814,2.283 0.814,1.358 1.386,0.786 C1.957,0.215 2.883,0.214 3.454,0.785 L13.270,10.601 C13.524,10.855 13.658,11.179 13.686,11.511 Z" />
                    </svg>
                </button>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="flex justify-center mt-4">
                <span>Loading reviews...</span>
            </div>

            <!-- Error State -->
            <div v-if="error" class="flex justify-center mt-4 text-red-500">
                <span>{{ error }}</span>
            </div>
        </div>
    </section>
</template>

<script setup>

import { ref, computed, onMounted } from 'vue';

const props = defineProps({
    headingText: {
        type: String,
        default: "Hear from People Loving the New Lifeboost Cognition Coffee, Upgraded with 100% Real Mushrooms"
    },
    productId: {
        type: String,
        default: "7387365703740"
    }
});

const reviews = ref([]);
const currentPage = ref(1);
const reviewsPerPage = ref(6);
const totalPages = ref(1);
const loading = ref(false);
const error = ref(null);
const hasMoreReviews = ref(true);
const ratings = ref();

const reviewsSection = ref(null);

const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0'); 
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
};

const fetchReviews = async (productId, page = 1) => {
    loading.value = true;
    error.value = null;

    try {
        const response = await fetch(
            `/api/fetchReviews?product_id=${productId}&page=${page}&limit=${reviewsPerPage.value}`
        );

        fetchRaitingReview(productId, 'cognition-mushroom-coffee');

        if (!response.ok) {
            throw new Error('Failed to fetch reviews');
        }

        const data = await response.json();
        if (Array.isArray(data.reviews)) {
            reviews.value = data.reviews;
            totalPages.value = Math.ceil((ratings.value?.reviews_count || 0) / reviewsPerPage.value);
            hasMoreReviews.value = data.reviews.length === reviewsPerPage.value;
        } else {
            reviews.value = [];
            hasMoreReviews.value = false;
        }
    } catch (err) {
        error.value = err.message || 'An error occurred while fetching reviews.';
    } finally {
        loading.value = false;
    }
};

const fetchRaitingReview = async (productId, handle) => {
    try {
        const response = await fetch(
            `/api/fetchReviewsCount?product_id=${productId}&handle=${handle}`
        );

        if (!response.ok) {
            throw new Error('Failed to fetch reviews');
        }

        const data = await response.json();
        ratings.value = data;
    } catch (err) {
        error.value = err.message || 'An error occurred while fetching reviews.';
    } finally {
        loading.value = false;
    }
};

const changePage = (page) => {
    if (page < 1 || page > totalPages.value) return;
    currentPage.value = page;
    fetchReviews(props.productId, currentPage.value);

    if (reviewsSection.value) {
        reviewsSection.value.scrollIntoView({ behavior: 'smooth' });
    }
};

const visiblePages = computed(() => {
    let start = Math.max(1, currentPage.value - 1);
    let end = Math.min(totalPages.value, currentPage.value + 2);

    if (end - start < 3 && start > 1) {
        start = Math.max(1, end - 3);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

onMounted(() => {
    fetchRaitingReview(props.productId, 'cognition-mushroom-coffee').then(() => {
        fetchReviews(props.productId, currentPage.value);
    });
});

defineBuilderComponent({
    friendlyName: "Review Section With Heading And ProductId",
    inputs: [
        {
            name: "headingText",
            type: "richText",
            friendlyName: "Heading Text",
            defaultValue: "Hear from People Loving the New Lifeboost Cognition Coffee, Upgraded with 100% Real Mushrooms",
        },
        {
            name: "productId",
            type: "string",
            friendlyName: "Product ID",
            defaultValue: "7387365703740",
        },
    ]
});
</script>
